import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { useSettings } from '../context/SettingsContext.jsx';
import { Swal2 } from '../lib/swal.js';
import { MORPH_BTN } from '../lib/ui.js';
import TimePicker from './TimePicker.jsx';
import DatePicker from './DatePicker.jsx';

// Port persis dari includes/contact-form.php — dipakai di halaman /kontak.
// Beda dari ContactInfoSection.jsx (includes/contact.php) yang dipakai di homepage.
export default function ContactSection() {
  const { settings } = useSettings();
  const [layananList, setLayananList] = useState([]);
  const [form, setForm] = useState({ nama: '', email: '', telepon: '', institusi: '', tanggal_acara: '', jam_acara: '', paket_layanan: '', pesan: '' });
  const [submitting, setSubmitting] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  useEffect(() => {
    api.get('/layanan').then(({ data }) => setLayananList(data.layanan || []));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/kontak', form);
      Swal2.success('Pesan Terkirim!', 'Tim kami akan menghubungi Anda segera melalui <strong>WhatsApp</strong> atau <strong>Email</strong>.<br><br>Terima kasih telah menghubungi kami!');
      setForm({ nama: '', email: '', telepon: '', institusi: '', tanggal_acara: '', jam_acara: '', paket_layanan: '', pesan: '' });
    } catch (err) {
      Swal2.toast('error', err.response?.data?.message || 'Gagal mengirim pesan. Silakan coba lagi.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3.5 rounded-xl border text-sm outline-none bg-neutral-50 dark:bg-neutral-900/50 text-neutral-900 dark:text-white focus:bg-white dark:focus:bg-neutral-900 border-neutral-200 dark:border-neutral-700 transition-all duration-200 focus:ring-2 focus:ring-forest-500/20 focus:border-forest-500';
  const labelCls = 'block text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-400 mb-2';

  const waLink = settings.whatsapp ? `https://wa.me/${settings.whatsapp}` : null;

  return (
    <section id="kontak" className="py-20 md:py-28 dark:bg-neutral-900 transition-colors duration-300" style={{ backgroundImage: 'linear-gradient(180deg, transparent, color-mix(in oklab, var(--accent) 4%, transparent), transparent)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold mb-4 border" style={{ background: 'color-mix(in oklab, var(--accent) 8%, transparent)', borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)', color: 'var(--accent)' }}>
            <span className="material-symbols-outlined text-base">mail</span> KONTAK
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 tracking-tight">
            Mulai Rencanakan <span style={{ color: 'var(--accent)' }}>Event Hewan</span> Anda
          </h2>
          <p className="text-neutral-500 dark:text-neutral-400 max-w-xl mx-auto text-base md:text-lg">
            Hubungi kami sekarang dan tim profesional kami siap membantu mewujudkan event impian Anda.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-stretch">
          {/* Form */}
          <div className="lg:col-span-3 rounded-3xl p-6 sm:p-8 border shadow-sm bg-white dark:bg-neutral-800 transition-all" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
            <form onSubmit={submit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls}>Nama Lengkap *</label>
                  <input required type="text" placeholder="nama lengkap" className={inputCls} value={form.nama} onChange={set('nama')} />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" placeholder="email@contoh.com" className={inputCls} value={form.email} onChange={set('email')} />
                </div>
                <div>
                  <label className={labelCls}>Telepon</label>
                  <input type="tel" placeholder="+62 xxx-xxx-xxxx" className={inputCls} value={form.telepon} onChange={set('telepon')} />
                </div>
                <div>
                  <label className={labelCls}>Institusi</label>
                  <input type="text" placeholder="Nama institusi / sekolah" className={inputCls} value={form.institusi} onChange={set('institusi')} />
                  <small className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 block">Opsional - untuk acara sekolah/instansi</small>
                </div>
              </div>

              <div>
                <label className={labelCls}>Tanggal Acara</label>
                <button
                  type="button"
                  onClick={() => setDatePickerOpen(true)}
                  className={`${inputCls} text-left flex items-center justify-between`}
                >
                  <span className={form.tanggal_acara ? '' : 'text-neutral-400'}>{form.tanggal_acara || 'Pilih tanggal'}</span>
                  <span className="material-symbols-outlined text-base text-neutral-500">calendar_month</span>
                </button>
                <small className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 block">📅 Rencana tanggal acara (opsional)</small>
              </div>

              <div>
                <label className={labelCls}>Jam Acara</label>
                <button
                  type="button"
                  onClick={() => setTimePickerOpen(true)}
                  className={`${inputCls} text-left flex items-center justify-between`}
                >
                  <span className={form.jam_acara ? '' : 'text-neutral-400'}>{form.jam_acara || 'Pilih jam'}</span>
                  <span className="material-symbols-outlined text-base text-neutral-500">schedule</span>
                </button>
                <small className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 block">⏰ Perkiraan jam mulai acara (opsional, default 08:00)</small>
              </div>

              <div>
                <label className={labelCls}>Paket Layanan</label>
                <div className="relative">
                  <select className={`${inputCls} appearance-none`} value={form.paket_layanan} onChange={set('paket_layanan')}>
                    <option value="">Pilih paket layanan</option>
                    {layananList.map((l) => <option key={l.id} value={l.nama}>{l.nama}</option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-neutral-500">
                    <span className="material-symbols-outlined text-xs">expand_more</span>
                  </div>
                </div>
              </div>

              <div>
                <label className={labelCls}>Pesan / Kebutuhan Event</label>
                <textarea rows={4} placeholder="Ceritakan kebutuhan event Anda secara detail..." className={`${inputCls} resize-none`} value={form.pesan} onChange={set('pesan')} />
              </div>

              <button disabled={submitting} type="submit" className={`w-full py-4 rounded-xl font-bold text-white text-sm tracking-wide shadow-md hover:shadow-xl disabled:opacity-60 flex items-center justify-center gap-2 ${MORPH_BTN}`} style={{ background: 'linear-gradient(135deg, var(--accent), var(--accent-hover))' }}>
                <span className="material-symbols-outlined text-base">send</span>
                {submitting ? 'MENGIRIM...' : 'KIRIM PESAN SEKARANG'}
              </button>
            </form>
          </div>

          {/* Info + Map */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="rounded-3xl p-8 text-white flex flex-col justify-between shadow-lg" style={{ background: 'linear-gradient(135deg, var(--accent), var(--color-forest-700))' }}>
              <div>
                <h3 className="font-display text-xl font-bold mb-6 tracking-wide uppercase text-white/90">Informasi Kontak</h3>
                <div className="space-y-6">
                  <InfoRow icon="location_on" label="Alamat Kantor" value={settings.alamat} />
                  <InfoRow icon="call" label="Telepon / HotLine" value={settings.telepon} />
                  <InfoRow icon="mail" label="Email" value={settings.email} />
                </div>
              </div>

              {waLink && (
                <a href={waLink} target="_blank" rel="noreferrer"
                  className="mt-8 w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold text-sm transition-all duration-300 shadow-md hover:shadow-lg hover:!bg-[#25D366] hover:!text-white hover:rounded-2xl active:rounded-3xl bg-white text-[#25D366] active:scale-[0.98]">
                  <WaIcon />
                  Chat via WhatsApp
                </a>
              )}
            </div>

            {settings.maps_embed_url && (
              <div className="rounded-3xl overflow-hidden border flex-1 shadow-sm" style={{ minHeight: 220, borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
                <iframe src={settings.maps_embed_url} width="100%" height="100%" style={{ border: 0, minHeight: 220 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="w-full h-full" title="Lokasi" />
              </div>
            )}
          </div>
        </div>
      </div>

      <DatePicker
        open={datePickerOpen}
        value={form.tanggal_acara}
        onClose={() => setDatePickerOpen(false)}
        onSave={(val) => { setForm((f) => ({ ...f, tanggal_acara: val })); setDatePickerOpen(false); }}
      />
      <TimePicker
        open={timePickerOpen}
        value={form.jam_acara}
        onClose={() => setTimePickerOpen(false)}
        onSave={(val) => { setForm((f) => ({ ...f, jam_acara: val })); setTimePickerOpen(false); }}
      />
    </section>
  );
}

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-inner" style={{ background: 'rgba(255,255,255,0.12)' }}>
        <span className="material-symbols-outlined text-base" style={{ color: '#fde68a' }}>{icon}</span>
      </div>
      <div>
        <div className="text-xs text-white/60 font-semibold uppercase tracking-wider mb-1">{label}</div>
        <div className="text-sm leading-relaxed text-white/90">{value}</div>
      </div>
    </div>
  );
}

function WaIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-[18px] h-[18px] shrink-0">
      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
    </svg>
  );
}
